import { ChangeDetectionStrategy, Component, input, linkedSignal, output, signal, inject } from '@angular/core';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { DateTime } from 'luxon';
import { map } from 'rxjs/operators';
import { SessionService } from '../../services/session.service';
import { Account, Credential, CredentialAccountUpdateChanges } from '../../types';
import { AccountService } from '../../services/account.service';
import { AsyncPipe, TitleCasePipe, CurrencyPipe } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { UiFieldComponent } from '../../ui/field/field.component';
import { UiInputDirective } from '../../ui/input/input.directive';
import { MinDateDirective } from '../../shared/forms/min-date.directive';
import { UiCheckboxComponent } from '../../ui/checkbox/checkbox.component';
import { UiButtonDirective } from '../../ui/button/button.directive';
import { DrawerFooterComponent } from '../../drawer/drawer-layout/drawer-footer.component';
import { DrawerSectionComponent } from '../../drawer/drawer-layout/drawer-section.component';

@Component({
  selector: 'app-account-details',
  templateUrl: './account-details.component.html',
  styleUrl: './account-details.component.scss',
  host: { class: 'flex flex-1 flex-col' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    UiFieldComponent,
    UiInputDirective,
    MinDateDirective,
    UiCheckboxComponent,
    UiButtonDirective,
    DrawerFooterComponent,
    DrawerSectionComponent,
    AsyncPipe,
    TitleCasePipe,
    CurrencyPipe,
  ],
})
export class AccountDetailsComponent {
  private accountService = inject(AccountService);
  private session = inject(SessionService);

  readonly account = input.required<Account>();
  readonly credential = input.required<Credential>();
  readonly accountSaved = output<number>();
  readonly saving = signal(false);
  // Editable copies of the account fields. They reset when a different account is
  // shown, but keep in-progress edits when the same account refreshes.
  readonly nickname = this.linkedToAccount(account => account.nickname);
  readonly syncDisabled = this.linkedToAccount(account => account.syncDisabled);
  readonly includeInAvailableBalance = this.linkedToAccount(account => account.includeInAvailableBalance);
  readonly syncStartDate = this.linkedToAccount<string | undefined>(account => this.buildSyncStartDate(account));
  readonly syncStartDateErrorId = 'account-details-sync-start-date-error';
  readonly updateCredentialAccountMutation = injectMutation(() =>
    this.accountService.updateExistingCredentialAccountMutationOptions(),
  );

  minimumDateValue$ = this.session.session$.pipe(
    map(session => this.formatMinimumDate(session?.account.startMonthYear, 'yyyy-MM-dd')),
  );
  minimumDateLabel$ = this.session.session$.pipe(
    map(session => this.formatMinimumDate(session?.account.startMonthYear, 'MM/dd/yyyy')),
  );

  get negativeBalance(): boolean {
    const account = this.account();
    return account.type === 'loan' || account.type === 'credit';
  }

  async addAccountDetailsSave() {
    this.saving.set(true);
    const account = this.account();
    try {
      await this.updateCredentialAccountMutation.mutateAsync({
        credentialId: this.credential().id,
        account,
        changes: this.buildAccountUpdateChanges(),
      });
      this.accountSaved.emit(1);
    } catch {
      // Keep the edit drawer open; the mutation error state is available to the template if surfaced later.
    } finally {
      this.saving.set(false);
    }
  }

  private linkedToAccount<T>(pick: (account: Account) => T) {
    return linkedSignal<Account, T>({
      source: this.account,
      computation: (account, previous) =>
        previous && previous.source.id === account.id ? previous.value : pick(account),
    });
  }

  buildSyncStartDate(account = this.account()) {
    if (!account || !account.syncStartDate) return DateTime.local().toFormat('yyyy-MM-dd');
    return DateTime.fromISO(account.syncStartDate, { zone: 'utc' }).toFormat('yyyy-MM-dd');
  }

  private buildAccountUpdateChanges(): CredentialAccountUpdateChanges {
    return {
      nickname: this.nickname(),
      syncDisabled: this.syncDisabled(),
      includeInAvailableBalance: this.includeInAvailableBalance(),
      syncStartDate: this.syncStartDate() ?? undefined,
    };
  }

  private formatMinimumDate(startMonthYear: string | undefined, format: string): string {
    return DateTime.fromISO(startMonthYear ?? DateTime.local().startOf('month').toISODate(), { zone: 'utc' }).toFormat(
      format,
    );
  }
}
